(() => {
  if (customElements.get('bc-sol-demo')) return;
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  let instance = 0;
  class SolutionDemo extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.timers = [];
      this.data = JSON.parse(this.querySelector('[data-sol-copy]')?.textContent || '{}');
      this.mode = this.dataset.solMode;
      this.context = this.dataset.solContext;
      this.locale = document.documentElement.lang || 'en';
      this.money = n => new Intl.NumberFormat(this.locale, {style: 'currency', currency: 'EUR', maximumFractionDigits: 0}).format(n);
      this.number = n => new Intl.NumberFormat(this.locale, {maximumFractionDigits: 2}).format(n);
      this.count = 0;
      this.id ||= `SolutionDemo-${++instance}`;
      const signal = this.abort.signal;
      this.addEventListener('click', event => this.onClick(event), {signal});
      this.addEventListener('input', event => this.onInput(event), {signal});
      this.addEventListener('change', event => {
        if (event.target.matches('[data-sol-market]')) this.market(event.target.value);
      }, {signal});
      this.setupTabs();
      if (this.mode === 'fashion' && this.context === 'hero') this.brand('cold');
      if (this.mode === 'international') this.market('es');
      if (this.mode === 'speed' && this.context === 'detail') this.budget();
      if (this.mode === 'custom' && this.context === 'detail') this.replacement();
      if (this.mode === 'economics') this.economics();
      const enter = () => {
        if (this.entered) return;
        this.entered = true;
        this.classList.add('is-visible');
        if (this.mode === 'speed' && this.context === 'hero') this.profile(0);
        if (this.mode === 'mix') this.mix(true);
      };
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) { enter(); this.observer.unobserve(this); }
        }), {threshold: .15});
        this.observer.observe(this);
      } else enter();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); this.timers?.forEach(clearTimeout);
    }
    ui(key) { return this.data.ui?.[key] || key; }
    text(selector, value) { const node = this.querySelector(selector); if (node) node.textContent = value; }
    selected(selector, active, attr = 'aria-selected') {
      this.querySelectorAll(selector).forEach(button => {
        const isActive = button === active;
        button.setAttribute(attr, String(isActive));
        if (button.getAttribute('role') === 'tab') button.tabIndex = isActive ? 0 : -1;
      });
      const panel = this.querySelector('[role="tabpanel"]');
      if (panel && active?.id) panel.setAttribute('aria-labelledby', active.id);
    }
    setupTabs() {
      const tabs = [...this.querySelectorAll('[role="tab"]')];
      if (!tabs.length) return;
      const panel = this.querySelector('.bc-sol-products, .bc-sol-message, .bc-sol-waterfall, .bc-sol-launch__scene');
      if (panel) { panel.id = `${this.id}-panel`; panel.setAttribute('role', 'tabpanel'); }
      tabs.forEach((tab, index) => {
        tab.id = `${this.id}-tab-${index}`;
        tab.tabIndex = index === 0 ? 0 : -1;
        if (panel) tab.setAttribute('aria-controls', panel.id);
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); tabs[next].click(); tabs[next].focus({preventScroll: true}); }
        }, {signal: this.abort.signal});
      });
      if (panel) panel.setAttribute('aria-labelledby', tabs[0].id);
    }
    onClick(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;
      if (target.hasAttribute('data-sol-brand')) {
        this.brand(target.dataset.solBrand); this.selected('[data-sol-brand]', target);
      }
      if (target.hasAttribute('data-sol-add')) {
        this.count += 1; this.text('[data-sol-cart]', this.count); this.text('[data-sol-added]', this.ui('added'));
      }
      if (target.hasAttribute('data-sol-phase')) {
        const phase = Number(target.dataset.solPhase);
        this.selected('[data-sol-phase]', target);
        this.querySelector('[data-sol-launch-progress]').style.width = `${[0, 50, 100][phase]}%`;
        this.querySelectorAll('.bc-sol-launch__track > i').forEach((node, i) => node.classList.toggle('is-on', i <= phase));
        this.text('[data-sol-launch-status]', [this.ui('early_access'), this.ui('collection_live'), this.ui('next_chapter')][phase]);
        this.text('[data-sol-launch-message]', [this.ui('prelaunch_message'), this.ui('launch_message'), this.ui('postlaunch_message')][phase]);
        this.dataset.phase = String(phase);
      }
      if (target.hasAttribute('data-sol-market-row')) this.market(target.dataset.solMarketRow);
      if (target.hasAttribute('data-sol-message')) {
        this.selected('[data-sol-message]', target); this.message(Number(target.dataset.solMessage));
      }
      if (target.hasAttribute('data-sol-offer-choice')) {
        this.selected('[data-sol-offer-choice]', target, 'aria-pressed');
        this.text('[data-sol-offer-result]', this.ui(target.dataset.solOfferChoice === 'yes' ? 'no_offer' : 'remind_offer'));
      }
      if (target.hasAttribute('data-sol-profile-tab')) {
        this.selected('[data-sol-profile-tab]', target); this.profile(Number(target.dataset.solProfileTab));
      }
      if (target.hasAttribute('data-sol-profile-run')) {
        this.profile(Number(this.querySelector('[data-sol-profile-tab][aria-selected="true"]')?.dataset.solProfileTab || 0));
      }
      if (target.hasAttribute('data-sol-feature')) {
        const index = Number(target.dataset.solFeature);
        this.selected('[data-sol-feature]', target, 'aria-pressed');
        this.text('[data-sol-feature-file]', ['cart-drawer.liquid', 'bundle-builder.liquid', 'store-locator.liquid', 'countdown-banner.liquid'][index]);
        this.text('[data-sol-feature-code]', this.ui(['cart_drawer', 'bundle', 'locator', 'countdown'][index]));
        this.querySelector('[data-sol-signal]').setAttribute('d', `M0 ${[30,94,158,222][index]}C60 ${[30,94,158,222][index]} 30 125 100 125`);
        this.classList.remove('is-replacing');
        void this.offsetWidth;
        this.classList.add('is-replacing');
      }
      if (target.hasAttribute('data-sol-mix')) this.mix(target.dataset.solMix === 'after');
    }
    onInput(event) {
      if (event.target.hasAttribute('data-sol-budget')) this.budget();
      if (event.target.hasAttribute('data-sol-app-price') || event.target.hasAttribute('data-sol-replace')) this.replacement();
      if (event.target.hasAttribute('data-sol-econ')) this.economics();
    }
    value(selector, fallback = 0) {
      const input = this.querySelector(selector), number = input?.valueAsNumber;
      if (!Number.isFinite(number)) return fallback;
      return Math.min(Number(input.max) || Infinity, Math.max(Number(input.min) || 0, number));
    }
    brand(brand) {
      (this.data.products?.[brand] || []).forEach((product, index) => {
        const image = this.querySelector(`[data-sol-product-image="${index}"]`);
        image.src = product.image; image.alt = product.name;
        this.text(`[data-sol-product-name="${index}"]`, product.name);
        this.text(`[data-sol-product-price="${index}"]`, new Intl.NumberFormat(this.locale, {style: 'currency', currency: 'EUR'}).format(product.price));
      });
      this.querySelector('[data-sol-cold-logo]').hidden = brand !== 'cold';
      this.querySelector('[data-sol-yuxus-logo]').hidden = brand !== 'yuxus';
    }
    market(key) {
      const data = {
        es: ['🇪🇸','España','Tu próximo favorito.','Español','EUR €','Knitwear',0],
        us: ['🇺🇸','United States','Your next everyday essential.','English','USD $','Hoodies',1],
        gb: ['🇬🇧','United Kingdom','Built for your everyday.','English','GBP £','Layers',0],
        fr: ['🇫🇷','France','Votre prochain essentiel.','Français','EUR €','Essentials',1],
        de: ['🇩🇪','Deutschland','Dein neues Lieblingsteil.','Deutsch','EUR €','Outerwear',0]
      }[key];
      if (!data) return;
      const select = this.querySelector('[data-sol-market]'); if (select) select.value = key;
      ['flag','country','headline','language','currency','product'].forEach((name, index) => this.text(`[data-sol-market-${name}]`, data[index]));
      const image = this.querySelector('[data-sol-market-image]'); if (image) image.src = this.data.products.cold[data[6]].image;
      this.querySelectorAll('[data-sol-market-row]').forEach(row => {
        row.classList.toggle('is-active', row.dataset.solMarketRow === key);
        row.setAttribute('aria-pressed', String(row.dataset.solMarketRow === key));
      });
    }
    message(index) {
      this.text('[data-sol-message-title]', this.data.messages[index]);
      this.text('[data-sol-message-time]', ['Day 0','+ 4h','+ 2 days'][index]);
      this.querySelector('[data-sol-offer]').hidden = index !== 1;
      this.dataset.message = String(index);
    }
    profile(index) {
      this.timers.forEach(clearTimeout); this.timers = [];
      this.text('[data-sol-profile-status]', this.ui('running'));
      const widths = [[12,39,19,27,16],[12,32,17,39,21],[10,15,16,45,18]][index];
      const offsets = [0,10,8,35,58];
      this.querySelectorAll('[data-sol-waterfall-bar]').forEach((bar, i) => {
        bar.style.setProperty('--sol-left', `${offsets[i]}%`);
        bar.style.setProperty('--sol-width', `${widths[i]}%`);
      });
      this.classList.remove('is-profiling'); void this.offsetWidth;
      this.classList.add('is-profiling');
      if (reduceMotion()) this.text('[data-sol-profile-status]', this.ui('ready'));
      else this.timers.push(setTimeout(() => this.text('[data-sol-profile-status]', this.ui('ready')), 2200));
    }
    budget() {
      const amounts = ['images','scripts','styles'].map(key => this.value(`[data-sol-budget="${key}"]`));
      const total = amounts.reduce((a,b) => a+b, 0), speed = this.value('[data-sol-budget="network"]', .5);
      this.text('[data-sol-transfer]', `${this.number(total * 8 / (Math.max(.5, speed) * 1000))} s`);
      this.text('[data-sol-total-weight]', `${this.number(total)} KB`);
      ['images','scripts','styles'].forEach((key,i) => this.querySelector(`[data-sol-weight="${key}"]`).style.width = `${total ? amounts[i]/total*100 : 0}%`);
    }
    replacement() {
      let monthly = 0, count = 0, last = 0;
      this.querySelectorAll('[data-sol-replace]').forEach((input, index) => {
        if (input.checked) { monthly += this.value(`[data-sol-app-price="${index}"]`); count += 1; last = index; }
        this.text(`[data-sol-replacement-state="${index}"]`, input.checked ? this.ui('custom_code') : 'App');
        input.closest('.bc-sol-replacement__row').classList.toggle('is-replaced', input.checked);
      });
      this.text('[data-sol-monthly]', this.money(monthly)); this.text('[data-sol-annual]', this.money(monthly * 12)); this.text('[data-sol-feature-count]', count);
      const image = this.querySelector('[data-sol-feature-preview]');
      if (image && this.data.previews) image.src = this.data.previews[last === 2 ? 1 : 0];
    }
    economics() {
      const value = key => this.value(`[data-sol-econ="${key}"]`);
      const net = Math.max(0, value('revenue') - value('discounts') - value('refunds'));
      const cogs = net * (1 - value('margin') / 100), shipping = value('fulfilment'), ads = value('marketing');
      const contribution = net - cogs - shipping - ads;
      this.text('[data-sol-contribution]', this.money(contribution));
      this.text('[data-sol-contribution-rate]', `${this.number(net ? contribution/net*100 : 0)}%`);
      this.text('[data-sol-net]', this.money(net)); this.text('[data-sol-cogs]', this.money(cogs));
      const heights = [net,cogs,shipping,ads,Math.max(0,contribution)];
      ['net','product','fulfilment','marketing','contribution'].forEach((key,i) => this.querySelector(`[data-sol-econ-bar="${key}"]`).style.height = `${net ? Math.max(2, Math.min(100, heights[i]/net*100)) : 2}%`);
      this.dataset.positive = String(contribution >= 0);
    }
    mix(after) {
      this.querySelector('[data-sol-spain-share]').style.width = `${after ? 25 : 95}%`;
      this.text('[data-sol-spain-number]', after ? '25%' : '95%');
      this.text('[data-sol-international-number]', after ? '75%' : '5%');
      this.querySelectorAll('[data-sol-mix]').forEach(button => button.setAttribute('aria-pressed', String((button.dataset.solMix === 'after') === after)));
    }
  }
  customElements.define('bc-sol-demo', SolutionDemo);
  const plans = document.querySelectorAll('[data-sol-reveal]');
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-active'); observer.unobserve(entry.target); }
    }), {threshold: .18});
    plans.forEach(plan => observer.observe(plan));
  } else plans.forEach(plan => plan.classList.add('is-active'));
})();
